/**
 * Reports.tsx — Saved gap analysis reports.
 *
 * Visible in all modes. Backend scope:
 *   - super_admin (no scope): all reports, last 50
 *   - demo / client: reports for that client only
 *
 * Row actions: Export (XLSX), View (→ /distribution-gaps?report=ID), Delete.
 */

import { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../api';
import { theme } from '../../theme';
import { useAdmin } from '../../AdminContext';
import {
  PageHeader,
  Button, IconButton,
  Select,
  Table, TableToolbar,
  Icon,
  EmptyState,
  Skeleton,
  toast,
} from '../../components/ui';

/* ═══════════════ TYPES ═══════════════ */

interface SavedReport {
  id: string;
  client_name: string;
  country: string;
  city: string | null;
  outlet_type: string | null;
  category: string;
  sub_category: string | null;
  currency: string;
  total_gap_outlets: number;
  total_opportunity_value: number;
  market_share_pct: number;
  created_at: string | null;
}

/* ═══════════════ HELPERS ═══════════════ */

function formatOpportunity(value: number, currency: string): string {
  const symbol = currency === 'EUR' ? '€'
    : currency === 'USD' ? '$'
    : currency === 'GBP' ? '£'
    : currency === 'RSD' ? 'RSD '
    : `${currency} `;
  if (value >= 1_000_000) return `${symbol}${(value / 1_000_000).toFixed(1)}M`;
  if (value >= 1_000) return `${symbol}${(value / 1_000).toFixed(0)}K`;
  return `${symbol}${value.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
}

function formatRelative(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  const ms = Date.now() - d.getTime();
  const sec = Math.floor(ms / 1000);
  if (sec < 60) return 'just now';
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const days = Math.floor(hr / 24);
  if (days < 7) return `${days}d ago`;
  return d.toLocaleDateString();
}

function fullDate(iso: string | null): string {
  if (!iso) return '';
  return new Date(iso).toLocaleString();
}

/* ═══════════════ PAGE ═══════════════ */

export default function Reports() {
  const navigate = useNavigate();
  const { adminMode, maskClientName, modeVersion } = useAdmin();

  const [reports, setReports] = useState<SavedReport[]>([]);
  const [loading, setLoading] = useState(true);
  const [exportingId, setExportingId] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  // Admin client filter
  const [clients, setClients] = useState<string[]>([]);
  const [clientFilter, setClientFilter] = useState('');

  useEffect(() => {
    document.title = 'Reports | Meridian Registry';
    return () => { document.title = 'Meridian Registry'; };
  }, []);

  /* ─── Fetch reports ─── */
  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    api.get<{ reports: SavedReport[] }>('/api/gaps/reports', { signal: controller.signal })
      .then(res => setReports(res.data.reports ?? []))
      .catch(err => {
        if (err?.name === 'CanceledError' || err?.code === 'ERR_CANCELED') return;
        setReports([]);
        toast.error('Failed to load reports');
      })
      .finally(() => setLoading(false));
    return () => controller.abort();
  }, [modeVersion]);

  /* ─── Fetch clients (admin only) for the filter dropdown ─── */
  useEffect(() => {
    if (!adminMode) { setClients([]); return; }
    const controller = new AbortController();
    api.get<string[]>('/api/clients', { signal: controller.signal })
      .then(r => setClients(r.data))
      .catch(() => {});
    return () => controller.abort();
  }, [adminMode, modeVersion]);

  /* ─── Filter ─── */
  const filtered = useMemo(() => {
    if (!adminMode || !clientFilter) return reports;
    return reports.filter(r => r.client_name === clientFilter);
  }, [reports, adminMode, clientFilter]);

  /* ─── Actions ─── */
  const handleExport = async (id: string) => {
    setExportingId(id);
    try {
      const res = await api.get(`/api/gaps/reports/${id}/export`, { responseType: 'blob' });
      const href = URL.createObjectURL(res.data);
      const link = document.createElement('a');
      link.href = href;
      link.download = 'gap_analysis.xlsx';
      const cd = res.headers['content-disposition'];
      if (cd) {
        const m = cd.match(/filename="?([^";\n]+)"?/);
        if (m) link.download = m[1];
      }
      link.click();
      URL.revokeObjectURL(href);
      toast.success('Download started');
    } catch {
      toast.error('Export failed');
    } finally {
      setExportingId(null);
    }
  };

  const handleView = (id: string) => {
    navigate(`/distribution-gaps?report=${id}`);
  };

  const handleDelete = async (id: string, scopeLabel: string) => {
    if (!window.confirm(`Delete this ${scopeLabel} report? This cannot be undone.`)) return;
    setDeletingId(id);
    try {
      await api.delete(`/api/gaps/reports/${id}`);
      setReports(prev => prev.filter(r => r.id !== id));
      toast.success('Report deleted');
    } catch {
      toast.error('Failed to delete report');
    } finally {
      setDeletingId(null);
    }
  };

  /* ─── Columns ─── */
  const columns = [
    {
      key: 'created_at',
      header: 'Date',
      width: 130,
      render: (r: SavedReport) => (
        <span
          title={fullDate(r.created_at)}
          style={{
            color: theme.text.secondary,
            fontFamily: theme.font.data,
            fontSize: theme.fontSize.sm,
          }}>
          {formatRelative(r.created_at)}
        </span>
      ),
    },
    // Client column — admin only
    ...(adminMode ? [{
      key: 'client_name',
      header: 'Client',
      render: (r: SavedReport) => (
        <span style={{ color: theme.text.primary, fontWeight: theme.fontWeight.medium }}>
          {maskClientName(r.client_name)}
        </span>
      ),
    }] : []),
    {
      key: 'scope',
      header: 'Scope',
      render: (r: SavedReport) => (
        <span style={{ color: theme.text.secondary, fontSize: theme.fontSize.sm }}>
          {[r.country, r.city || 'All cities', r.outlet_type].filter(Boolean).join(' · ')}
        </span>
      ),
    },
    {
      key: 'category',
      header: 'Category',
      render: (r: SavedReport) => (
        <span style={{ color: theme.text.primary, fontSize: theme.fontSize.sm }}>
          {r.category}
          {r.sub_category && (
            <span style={{ color: theme.text.tertiary }}>{` · ${r.sub_category}`}</span>
          )}
        </span>
      ),
    },
    {
      key: 'total_gap_outlets',
      header: 'Gap outlets',
      align: 'right' as const,
      width: 110,
      render: (r: SavedReport) => (
        <span style={{
          fontFamily: theme.font.data,
          fontSize: theme.fontSize.sm,
          color: theme.text.primary,
          fontVariantNumeric: 'tabular-nums',
        }}>{r.total_gap_outlets.toLocaleString()}</span>
      ),
    },
    {
      key: 'total_opportunity_value',
      header: 'Opportunity',
      align: 'right' as const,
      width: 120,
      render: (r: SavedReport) => (
        <span style={{
          fontFamily: theme.font.data,
          fontSize: theme.fontSize.sm,
          color: theme.brand.cyanLight,
          fontVariantNumeric: 'tabular-nums',
          fontWeight: theme.fontWeight.medium,
        }}>{formatOpportunity(r.total_opportunity_value, r.currency)}</span>
      ),
    },
    {
      key: 'market_share_pct',
      header: 'Market share',
      align: 'right' as const,
      width: 110,
      render: (r: SavedReport) => (
        <span style={{
          fontFamily: theme.font.data,
          fontSize: theme.fontSize.sm,
          color: theme.text.secondary,
          fontVariantNumeric: 'tabular-nums',
        }}>{`${r.market_share_pct}%`}</span>
      ),
    },
    {
      key: 'actions',
      header: '',
      width: 170,
      render: (r: SavedReport) => (
        <div style={{ display: 'flex', gap: theme.space[1], justifyContent: 'flex-end' }}>
          <IconButton
            icon={<Icon.Download size={13} />}
            size="sm"
            aria-label="Export Excel"
            title="Export Excel"
            disabled={exportingId === r.id}
            onClick={(e) => { e.stopPropagation(); handleExport(r.id); }}
          />
          <IconButton
            icon={<Icon.Eye size={13} />}
            size="sm"
            aria-label="View report"
            title="View report"
            onClick={(e) => { e.stopPropagation(); handleView(r.id); }}
          />
          <IconButton
            icon={<Icon.Trash size={13} />}
            size="sm"
            aria-label="Delete report"
            title="Delete report"
            disabled={deletingId === r.id}
            onClick={(e) => { e.stopPropagation(); handleDelete(r.id, r.category); }}
          />
        </div>
      ),
    },
  ];

  /* ─── Render ─── */

  if (loading && reports.length === 0) {
    return (
      <div style={{ padding: `${theme.space[8]} ${theme.space[10]}`, maxWidth: 1600, margin: '0 auto' }}>
        <PageHeader title="Reports" description="Saved gap analysis reports — export, view, or delete." />
        <div style={{ marginTop: theme.space[5] }}>
          {[0, 1, 2, 3, 4].map(i => (
            <div key={i} style={{ marginBottom: 10 }}>
              <Skeleton width="100%" height={44} />
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (!loading && reports.length === 0) {
    return (
      <div style={{ padding: `${theme.space[8]} ${theme.space[10]}`, maxWidth: 1600, margin: '0 auto' }}>
        <PageHeader title="Reports" description="Saved gap analysis reports — export, view, or delete." />
        <div style={{ marginTop: theme.space[8] }}>
          <EmptyState
            icon={<Icon.Chart size={22} />}
            title="No reports generated yet"
            description="Generate your first gap analysis report from Distribution Gaps."
            action={
              <Button
                variant="primary"
                leftIcon={<Icon.Chart size={14} />}
                onClick={() => navigate('/distribution-gaps')}
              >Go to Distribution Gaps</Button>
            }
          />
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding: `${theme.space[8]} ${theme.space[10]}`, maxWidth: 1600, margin: '0 auto' }}>
      <PageHeader
        title="Reports"
        description="Saved gap analysis reports — export, view, or delete."
        meta={
          <>
            <span style={{ color: theme.text.primary, fontWeight: theme.fontWeight.medium }}>
              {filtered.length.toLocaleString()}
            </span>
            <span>report{filtered.length === 1 ? '' : 's'}</span>
          </>
        }
      />

      {/* Admin-only client filter */}
      {adminMode && clients.length > 0 && (
        <div style={{ display: 'flex', gap: theme.space[3], marginBottom: theme.space[5] }}>
          <div style={{ minWidth: 240 }}>
            <Select value={clientFilter} onChange={e => setClientFilter(e.target.value)}>
              <option value="">All clients</option>
              {clients.map(c => <option key={c} value={c}>{c}</option>)}
            </Select>
          </div>
        </div>
      )}

      <TableToolbar>
        <div style={{ color: theme.text.tertiary, fontSize: theme.fontSize.sm }}>
          Showing{' '}
          <span style={{ color: theme.text.primary, fontFamily: theme.font.data, fontWeight: theme.fontWeight.medium }}>
            {filtered.length.toLocaleString()}
          </span>
          {clientFilter && (
            <>
              {' '}of{' '}
              <span style={{ color: theme.text.primary, fontFamily: theme.font.data, fontWeight: theme.fontWeight.medium }}>
                {reports.length.toLocaleString()}
              </span>
            </>
          )}
        </div>
      </TableToolbar>

      <Table
        data={filtered}
        columns={columns}
        loading={loading}
        minWidth={adminMode ? 1100 : 960}
        rowKey={r => r.id}
        emptyState={
          <EmptyState
            icon={<Icon.Chart size={18} />}
            title="No reports for this filter"
            description="Try clearing the client filter."
          />
        }
      />
    </div>
  );
}
